import { useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { adminLogout } from '../services/api';
import { ThemeToggle } from './ThemeToggle';
import type { AdminPermission } from '@lighthouse/shared';

interface NavItem {
  to: string;
  label: string;
  permission: AdminPermission;
}

interface NavCategory {
  id: string;
  label: string;
  items: NavItem[];
}

const NAV_CATEGORIES: NavCategory[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', permission: 'dashboard:read' }],
  },
  {
    id: 'people',
    label: 'People & care',
    items: [
      { to: '/users', label: 'Users', permission: 'users:read' },
      { to: '/staff-chat', label: 'Staff Chat', permission: 'users:read' },
      { to: '/therapists', label: 'Therapists', permission: 'therapists:read' },
      { to: '/therapists/pending', label: 'Pending verification', permission: 'therapists:verify' },
      { to: '/sessions', label: 'Session moderation', permission: 'sessions:moderate' },
      { to: '/assessments', label: 'Assessments', permission: 'assessments:manage' },
    ],
  },
  {
    id: 'partners',
    label: 'Partners',
    items: [
      { to: '/corporate', label: 'Corporate approvals', permission: 'content:manage' },
      { to: '/campus', label: 'Campus partnerships', permission: 'content:manage' },
      { to: '/hmo-keys', label: 'HMO API keys', permission: 'hmo:manage' },
      { to: '/expansion', label: 'Expansion', permission: 'expansion:manage' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      { to: '/content', label: 'Resources', permission: 'content:manage' },
      { to: '/wellness', label: 'Wellness & heroes', permission: 'content:manage' },
      { to: '/engagement', label: 'Engagement', permission: 'content:manage' },
      { to: '/translations', label: 'Translations', permission: 'translations:manage' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { to: '/payouts', label: 'Payouts', permission: 'refunds:issue' },
      { to: '/wallet', label: 'Wallet & referrals', permission: 'refunds:issue' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights & compliance',
    items: [
      { to: '/reports', label: 'Wellness reports', permission: 'reports:generate' },
      { to: '/matching-bias', label: 'Matching bias', permission: 'reports:generate' },
      { to: '/audit-logs', label: 'Audit logs', permission: 'audit:read' },
      { to: '/dependencies', label: 'Dependency vulnerabilities', permission: 'audit:read' },
      { to: '/backups', label: 'Backups', permission: 'backups:manage' },
      { to: '/web-sessions', label: 'Web sessions', permission: 'admins:manage' },
      { to: '/live-sessions', label: 'Live sessions', permission: 'admins:manage' },
    ],
  },
];

function pathInCategory(pathname: string, items: NavItem[]): boolean {
  return items.some((item) => {
    if (item.to === '/') return pathname === '/';
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  });
}

export function AdminLayout() {
  const { logout } = useAdminAuth();
  const { hasPermission, loading: permissionsLoading } = useAdminPermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const visibleCategories = useMemo(
    () =>
      NAV_CATEGORIES.map((category) => ({
        ...category,
        items: category.items.filter((item) => hasPermission(item.permission)),
      })).filter((category) => category.items.length > 0),
    [hasPermission]
  );

  const activeCategoryId = useMemo(() => {
    const match = visibleCategories.find((c) => pathInCategory(pathname, c.items));
    return match?.id ?? visibleCategories[0]?.id ?? '';
  }, [pathname, visibleCategories]);

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeCategoryId) return;
    setOpenCategories((prev) => ({ ...prev, [activeCategoryId]: true }));
  }, [activeCategoryId]);

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = () => {
    void adminLogout();
    logout();
    navigate('/');
  };

  const isFullBleed = pathname === '/' || pathname.startsWith('/staff-chat');
  const currentCategory = visibleCategories.find((category) =>
    pathInCategory(pathname, category.items)
  );

  return (
    <div className="admin-shell flex h-screen">
      <nav className="glass-nav flex w-64 flex-col">
        <div className="flex h-16 items-center border-b border-white/10 px-4">
          <div className="admin-brand">
            <img
              src="/brand/mylighthouse-symbol-dark.svg"
              alt=""
              aria-hidden
              className="admin-brand-mark-img"
            />
            <div className="min-w-0">
              <p className="admin-brand-name">
                <span className="text-white">My</span>
                <span className="text-[var(--color-brand-primary,#0E6F5C)]">Lighthouse</span>
              </p>
              <p className="admin-brand-meta">Admin</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3">
          {!permissionsLoading &&
            visibleCategories.map((category) => {
              const isOpen = !!openCategories[category.id];
              return (
                <div key={category.id} className="admin-nav-category">
                  <button
                    type="button"
                    className="admin-nav-category-btn"
                    onClick={() => toggleCategory(category.id)}
                    aria-expanded={isOpen}
                  >
                    <span>{category.label}</span>
                    <span className={`admin-nav-chevron ${isOpen ? 'is-open' : ''}`} aria-hidden>
                      ›
                    </span>
                  </button>
                  {isOpen && (
                    <div className="admin-nav-category-items">
                      {category.items.map((item) => {
                        const isActive =
                          item.to === '/'
                            ? pathname === '/'
                            : pathname === item.to || pathname.startsWith(`${item.to}/`);
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={`admin-nav-link ${isActive ? 'admin-nav-link-active' : ''}`}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        <div className="border-t border-white/10 px-3 py-3">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full rounded-xl px-3 py-2 text-left text-sm font-semibold text-red-200 transition-colors hover:bg-white/10"
          >
            Log out
          </button>
        </div>
      </nav>

      <div className="admin-stage">
        <header className="admin-toolbar">
          <p className="admin-crumb">{currentCategory?.label ?? 'Admin'}</p>
          <ThemeToggle compact />
        </header>
        <main
          className={`flex min-h-0 flex-1 flex-col ${isFullBleed ? 'overflow-hidden' : 'overflow-y-auto'}`}
        >
          <div className={isFullBleed ? 'admin-canvas admin-canvas--bleed' : 'admin-canvas'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
